import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getResourcesData, formatDate, convertDriveUrl } from '../../services/googleSheets';
import '../NoticeList.css';
import ShareButton from '../ShareButton';
import URLPreview from '../URLPreview';

interface ResourceItem {
  id: number;
  카테고리: string;
  제목: string;
  내용: string;
  작성일: string;
  첨부파일URL?: string;
  첨부파일이름?: string;
  태그?: string;
  다운로드수?: number;
}

const Resources: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resources, setResources] = useState<ResourceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string>('전체');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedResource, setSelectedResource] = useState<ResourceItem | null>(null);
  const itemsPerPage = 10;

  const categories = ['전체', '교육자료', '연구보고서', '정책자료', '홍보자료', '양식서류'];

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const data = await getResourcesData();
        // 최신순으로 정렬 (자료실 시트에서 가져오므로 별도 필터링 불필요)
        const sortedData = data.sort((a: any, b: any) => b.id - a.id);
        setResources(sortedData as ResourceItem[]);
        
        // URL에 id가 있으면 해당 자료를 선택
        if (id) {
          const resource = sortedData.find((item: any) => item.id === parseInt(id));
          if (resource) {
            setSelectedResource(resource as ResourceItem);
          }
        }
      } catch (error) {
        console.error('자료실을 불러오는데 실패했습니다:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, [id]);

  // 검색 및 카테고리 필터링
  const filteredResources = resources.filter(item => {
    const matchesCategory = selectedCategory === '전체' || item.카테고리?.includes(selectedCategory);
    const matchesSearch = searchTerm === '' || 
      item.제목?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.내용?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.태그?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  // 페이지네이션 계산
  const totalPages = Math.ceil(filteredResources.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredResources.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleResourceClick = (resource: ResourceItem) => {
    navigate(`/resources/${resource.id}`);
  };

  const handleBackToList = () => {
    setSelectedResource(null);
    navigate('/resources');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // YouTube URL을 embed URL로 변환하는 함수
  const getYouTubeEmbedUrl = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    return null;
  };

  // 내용에서 YouTube 링크와 일반 URL을 찾아서 처리하는 함수
  const renderContentWithMediaAndLinks = (content: string) => {
    if (!content) return '';

    // YouTube URL 정규식
    const youtubeRegex = /(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)[\w-]+)/g;
    // 일반 URL 정규식 (YouTube 제외)
    const urlRegex = /(https?:\/\/(?!(?:www\.)?(?:youtube\.com|youtu\.be))[^\s<>"{}|\\^`[\]]+)/g;
    
    let processedContent = content;
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;
    let elementIndex = 0;

    // YouTube URL 찾기
    let youtubeMatch;
    const youtubeMatches: Array<{match: string, index: number}> = [];
    while ((youtubeMatch = youtubeRegex.exec(content)) !== null) {
      youtubeMatches.push({match: youtubeMatch[0], index: youtubeMatch.index});
    }

    // 일반 URL 찾기
    let urlMatch;
    const urlMatches: Array<{match: string, index: number}> = [];
    while ((urlMatch = urlRegex.exec(content)) !== null) {
      urlMatches.push({match: urlMatch[0], index: urlMatch.index});
    }

    // 모든 매치를 인덱스 순으로 정렬
    const allMatches = [...youtubeMatches.map(m => ({...m, type: 'youtube'})), ...urlMatches.map(m => ({...m, type: 'url'}))]
      .sort((a, b) => a.index - b.index);

    // 텍스트와 미디어를 순서대로 렌더링
    allMatches.forEach((match, i) => {
      // 이전 매치와 현재 매치 사이의 텍스트 추가
      if (match.index > lastIndex) {
        const textBefore = content.substring(lastIndex, match.index);
        if (textBefore.trim()) {
          elements.push(
            <span key={`text-${elementIndex++}`} style={{ whiteSpace: 'pre-line' }}>
              {textBefore}
            </span>
          );
        }
      }

      // 매치된 URL 처리
      if (match.type === 'youtube') {
        const embedUrl = getYouTubeEmbedUrl(match.match);
        if (embedUrl) {
          elements.push(
            <div key={`youtube-${elementIndex++}`} style={{ margin: '2rem 0' }}>
              <iframe
                width="100%"
                height="400"
                src={embedUrl}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
            </div>
          );
        }
      } else if (match.type === 'url') {
        elements.push(
          <URLPreview key={`url-${elementIndex++}`} url={match.match} />
        );
      }

      lastIndex = match.index + match.match.length;
    });

    // 마지막 매치 이후의 텍스트 추가
    if (lastIndex < content.length) {
      const textAfter = content.substring(lastIndex);
      if (textAfter.trim()) {
        elements.push(
          <span key={`text-${elementIndex++}`} style={{ whiteSpace: 'pre-line' }}>
            {textAfter}
          </span>
        );
      }
    }

    // 매치가 없으면 원본 텍스트 반환
    if (elements.length === 0) {
      return <span style={{ whiteSpace: 'pre-line' }}>{content}</span>;
    }

    return elements;
  };

  // 첨부파일 표시 컴포넌트
  const AttachmentDisplay: React.FC<{ url: string; fileName?: string }> = ({ url, fileName }) => {
    const downloadUrl = convertDriveUrl(url) || url;
    const displayName = fileName || '첨부파일';
    
    return (
      <a 
        href={downloadUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: '#28a745',
          textDecoration: 'none',
          fontSize: '0.9rem',
          padding: '0.5rem 1rem',
          border: '1px solid #28a745',
          borderRadius: '20px',
          transition: 'all 0.2s',
          fontWeight: '600'
        }}
        title={displayName}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#28a745';
          e.currentTarget.style.color = 'white';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = '#28a745';
        }}
      >
        <span>📥</span>
        <span>다운로드</span>
      </a>
    );
  };

  if (loading) {
    return (
      <div className="notice-loading">
        <div className="loading-spinner"></div>
        <p>자료실을 불러오는 중...</p>
      </div>
    );
  }

  // 상세보기 화면
  if (selectedResource) {
    return (
      <div className="notice-list-container">
        <div className="notice-header">
          <h1>📚 자료실</h1>
          <p>청소년 스마트폰 프리 운동본부의 교육 자료와 연구 보고서를 다운로드하세요</p>
        </div>
        <div className="notice-list" style={{ padding: '2rem' }}>
          <div className="notice-header" style={{ marginBottom: '2rem', textAlign: 'left' }}>
            <h1 style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>{selectedResource.제목}</h1>
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              fontSize: '0.9rem', 
              color: '#666',
              paddingBottom: '1rem',
              borderBottom: '2px solid #e74c3c'
            }}>
              <span>📅 {formatDate(selectedResource.작성일)}</span>
              <span>📂 {selectedResource.카테고리}</span>
            </div>
          </div>
          
          <div style={{ 
            lineHeight: '1.8', 
            color: '#2c3e50', 
            marginBottom: '2rem'
          }}>
            {renderContentWithMediaAndLinks(selectedResource.내용)}
          </div>
          
          {selectedResource.태그 && (
            <div style={{
              marginBottom: '2rem',
              paddingTop: '1rem',
              borderTop: '1px solid #eee'
            }}>
              <h3 style={{ color: '#2c3e50', marginBottom: '1rem', fontSize: '1.1rem' }}>
                태그
              </h3>
              <div>
                {selectedResource.태그.split(',').map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      background: '#e74c3c',
                      color: 'white',
                      padding: '0.5rem 1rem',
                      borderRadius: '20px',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      marginRight: '0.5rem',
                      marginBottom: '0.5rem',
                      display: 'inline-block'
                    }}
                  >
                    {tag.trim()}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {selectedResource.첨부파일URL && (
            <div style={{
              borderTop: '1px solid #eee',
              paddingTop: '1.5rem',
              marginBottom: '2rem'
            }}>
              <h3 style={{ color: '#2c3e50', marginBottom: '1rem', fontSize: '1.1rem' }}>
                첨부파일
              </h3>
              <AttachmentDisplay 
                url={selectedResource.첨부파일URL} 
                fileName={selectedResource.첨부파일이름}
              />
            </div>
          )}
          
          <div style={{
            borderTop: '1px solid #eee',
            paddingTop: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <button
              onClick={handleBackToList}
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#5a6268'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#6c757d'}
            >
              목록
            </button>
            <ShareButton title={`${selectedResource.제목} - 청소년 스마트폰 프리 운동`} />
          </div>
        </div>
      </div>
    );
  }

  // 목록 화면
  return (
    <div className="notice-list-container">
      <div className="notice-header">
        <h1>📚 자료실</h1>
        <p>청소년 스마트폰 프리 운동본부의 교육 자료와 연구 보고서를 다운로드하세요</p>
      </div>

      {/* 검색 및 필터 */}
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '15px',
        boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
        marginBottom: '2rem'
      }}>
        <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
          <input
            type="text"
            placeholder="자료명, 내용, 태그로 검색..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              width: '100%',
              padding: '1rem 3rem 1rem 1rem',
              border: '2px solid #e9ecef',
              borderRadius: '25px',
              fontSize: '1rem',
              outline: 'none',
              transition: 'border-color 0.2s',
              fontFamily: 'SUIT Variable, sans-serif'
            }}
            onFocus={(e) => e.target.style.borderColor = '#e74c3c'}
            onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
          />
          <span style={{
            position: 'absolute',
            right: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#6c757d',
            fontSize: '1.2rem'
          }}>🔍</span>
        </div>

        <div style={{
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          {categories.map(category => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              style={{
                padding: '0.75rem 1.5rem',
                border: '2px solid #e74c3c',
                background: selectedCategory === category ? '#e74c3c' : 'white',
                color: selectedCategory === category ? 'white' : '#e74c3c',
                borderRadius: '25px',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.2s',
                fontSize: '0.9rem',
                fontFamily: 'SUIT Variable, sans-serif'
              }}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      <div className="notice-stats">
        <span>전체 {filteredResources.length}건</span>
        <span>페이지 {currentPage} / {totalPages}</span>
      </div>

      <div className="notice-list">
        {currentItems.length > 0 ? (
          <>
            {/* 데스크톱 테이블 뷰 */}
            <table className="notice-table">
              <thead>
                <tr>
                  <th className="notice-number">번호</th>
                  <th style={{ width: '120px', textAlign: 'center' }}>분류</th>
                  <th className="notice-title-header">자료명</th>
                  <th className="notice-date-header">등록일</th>
                  <th style={{ width: '120px', textAlign: 'center' }}>다운로드</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item) => (
                  <tr key={item.id} className="notice-row">
                    <td className="notice-number">{item.id}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        color: 'white',
                        background: '#007bff'
                      }}>
                        {item.카테고리 || '자료'}
                      </span>
                    </td>
                    <td className="notice-title-cell">
                      <div>
                        <button 
                          onClick={() => handleResourceClick(item)}
                          className="notice-link"
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            cursor: 'pointer',
                            textAlign: 'left',
                            width: '100%',
                            padding: 0
                          }}
                        >
                          {item.제목 || '제목 없음'}
                        </button>
                        {item.태그 && (
                          <div style={{ marginTop: '0.5rem' }}>
                            {item.태그.split(',').map((tag, index) => (
                              <span
                                key={index}
                                style={{
                                  background: '#e9ecef',
                                  color: '#6c757d',
                                  padding: '0.2rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  marginRight: '0.25rem'
                                }}
                              >
                                {tag.trim()}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="notice-date-cell">{formatDate(item.작성일)}</td>
                    <td style={{ textAlign: 'center' }}>
                      {item.첨부파일URL && (
                        <AttachmentDisplay 
                          url={item.첨부파일URL} 
                          fileName={item.첨부파일이름}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 모바일 카드 뷰 */}
            <div className="notice-cards">
              {currentItems.map((item) => (
                <div key={item.id} className="notice-card">
                  <div className="notice-card-header">
                    <div className="notice-card-info">
                      <span className="notice-card-number">
                        📚 {item.id}
                      </span>
                      <span className="notice-card-date">
                        📅 {formatDate(item.작성일)}
                      </span>
                      <span className="notice-card-category">
                        📂 {item.카테고리 || '자료'}
                      </span>
                    </div>
                  </div>
                  
                  <div 
                    className="notice-card-title"
                    onClick={() => handleResourceClick(item)}
                  >
                    {item.제목 || '제목 없음'}
                  </div>
                  
                  {item.내용 && (
                    <div className="notice-card-content">
                      {item.내용.substring(0, 100)}...
                    </div>
                  )}
                  
                  {item.태그 && (
                    <div className="notice-card-tags">
                      {item.태그.split(',').map((tag, index) => (
                        <span key={index} className="notice-card-tag">
                          {tag.trim()}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="notice-card-footer">
                    <div></div>
                    {item.첨부파일URL && (
                      <AttachmentDisplay 
                        url={item.첨부파일URL} 
                        fileName={item.첨부파일이름}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="notice-empty">
            <p>검색 조건에 맞는 자료가 없습니다.</p>
          </div>
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            ‹ 이전
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
            >
              {page}
            </button>
          ))}
          
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            다음 ›
          </button>
        </div>
      )}
    </div>
  );
};

export default Resources; 